import { type Axios, isAxiosError } from "axios";

import type {
  BackendFilterableSearchRequest,
  BackendFilters,
} from "../../utils/backendFilters";
import { stringifyValues } from "../../utils/utils";

export interface ClaimsSearchParams {
  filters: BackendFilters;
  tab: string;
  page: number;
  pageSize: number;
  sortField: string | null | undefined;
  sortDirection: "asc" | "desc";
  view: string | null;
  deniseQueue: string | null;
  devMode: boolean;
}

export const getClaimsProceduresAndPatient = (
  c: Axios,
  params: ClaimsSearchParams,
  signal?: AbortSignal,
) => {
  const { filters: additionalFilters } = params;
  const filters: Record<string, string[]> = { ...additionalFilters.filters };

  const getSort = (): string[] => {
    if (params.sortField) return [params.sortField];
    if (params.sortField === null) return [];
    return ["posted_datetime", "urgency", "first_retrieved_date"];
  };

  const requestBody: BackendFilterableSearchRequest = {
    page: params.page,
    page_size: params.pageSize,
    sort: getSort(),
    sort_direction: params.sortDirection,
    filters,
  };

  if (additionalFilters.datagridFilters?.length) {
    requestBody.datagrid_filters = additionalFilters.datagridFilters;
  }
  if (additionalFilters.logicalOperator) {
    requestBody.logical_operator = additionalFilters.logicalOperator;
  }

  const hasContextFilters =
    requestBody.filters?.wieldy_id ||
    requestBody.filters?.patient_key ||
    requestBody.filters?.payment_key;

  const queryParams = new URLSearchParams();
  if (params.tab && !hasContextFilters) {
    queryParams.append("tab", params.tab);
  }
  if (params.view) {
    queryParams.append("view", params.view);
  }
  if (params.deniseQueue) {
    queryParams.append("denise_queue", params.deniseQueue);
  }
  if (params.devMode) {
    queryParams.append("dev_mode", "true");
  }

  const url = `/claims/search${queryParams.toString() ? `?${queryParams.toString()}` : ""}`;

  return c
    .post<SearchResponse>(url, requestBody, { signal })
    .then((r) => r.data);
};

export const postUpdate = async (
  c: Axios,
  body: Record<string, unknown>,
  opts?: { sync?: boolean },
): Promise<string> => {
  const { search } = window.location;
  const response = await c.post(
    `updates${search}`,
    opts?.sync ? { ...body, sync: true } : body,
  );
  return response.data.wieldyId;
};

export const updateClaim = (c: Axios, claimMessage: Partial<ClaimMessage>) =>
  postUpdate(c, { type: "Claim", payload: claimMessage, verb: "UPDATE" });

export const updateDenial = (
  c: Axios,
  claimWork: ClaimWork,
  denialWork?: DenialWork,
) => {
  const payload: Record<string, unknown> = {
    wieldyId: claimWork.wieldyId,
    practiceId: claimWork.practiceId,
    userAction: claimWork.action,
  };
  if (denialWork?.userAction !== undefined) {
    payload.userAction = denialWork.userAction;
  }
  if (denialWork?.urgency !== undefined) {
    payload.urgency = denialWork.urgency;
  }
  if (denialWork?.confirmAction !== undefined) {
    payload.confirmAction = denialWork.confirmAction;
  }
  return postUpdate(
    c,
    { type: "Denial", payload, verb: "UPDATE" },
    { sync: true },
  );
};

export const createClaim = (c: Axios, claim: ClaimMessage) =>
  postUpdate(c, {
    type: "Claim",
    payload: stringifyValues(claim),
    verb: "CREATE",
  });

export const updateStringifyClaim = (
  c: Axios,
  claimMessage: Partial<ClaimMessage>,
) =>
  postUpdate(c, {
    type: "Claim",
    payload: stringifyValues(claimMessage),
    verb: "UPDATE",
  });

export const batchPostToPMS = async (c: Axios) => {
  const { search } = window.location;
  await c.post(`pms${search}`);
};

export const getInsuranceArActionOptions = (c: Axios, signal?: AbortSignal) =>
  c
    .get<InsuranceArActionOptionsMessage>("/insurance-ar/action-options", {
      signal,
    })
    .then((r) => r.data);

export const getInsuranceArEntryForClaim = async (
  c: Axios,
  claimKey: string,
  practiceId: string,
  signal?: AbortSignal,
): Promise<InsuranceArEntryMessage | null> => {
  try {
    const response = await c.get<InsuranceArEntryMessage>(
      `/insurance-ar/claim/${claimKey}/entry`,
      { params: { practice_id: practiceId }, signal },
    );
    return response.data;
  } catch (error) {
    if (isAxiosError(error) && error.response?.status === 404) {
      return null;
    }
    throw error;
  }
};

export const updateInsuranceArEntry = (
  c: Axios,
  payload: InsuranceArEntryUpdatePayload,
) =>
  postUpdate(
    c,
    { type: "InsuranceArEntry", payload, verb: "UPDATE" },
    { sync: true },
  );
